import React, { memo, useState } from "react"
import {
    IMeetingServiceDetailAction,
    IMeetingServiceDetailOrderAction
} from "../../../../Types/MeetingPlanning/meetingServiceOrderDetail"
import { useToken } from "../../../Login/useToken"
import { useEnvironment } from "../../../Environment/envHooks"
import { useTransactionHandling } from "../../../Transaction/transactionHooks"
import { useApiCall } from "../../../Query/useApiCall"
import { LocalizedStrict } from "../../../Localization/Components/Localized"
import { ConfirmationDialog } from "../../../../Components/Dialog/ConfirmationDialog"
import { HeadlessReceiptSection, ReceiptAction, ReceiptSkeleton } from "../ReceiptBlocks"
import { ForwardReceiptDrawer } from "../ForwardReceiptDrawer"
import { MessageDialog } from "../../../../Components/Dialog/MessageDialog"
import OrderLockedAnim from "../../Assets/order-locked-lottie-anim.json?url"
import { ActionSheetButtonItem } from "../../../../Components/Dialog/Options/ActionSheetModalContent"
import OptionsDialog from "../../../../Components/Dialog/Options/OptionsDialog"
import { WebshopType } from "../../../../Types/appTypes"
import { DeliveryType, IEditMeetingOrderData } from "../../../../Types/MeetingPlanning/deliveryForm"
import { editMeetingOrder } from "../../../MeetingPlanning/Redux/EditOrder/MeetingPlanningEditOrderActions"
import { useDispatch } from "react-redux"
import { deleteMeetingServiceOrder } from "../../orderDetailsAPI"

enum ActionDialog {
    None = "None",
    ForwardReceipt = "ForwardReceipt",
    UpdateUnavailable = "UpdateUnavailable",
    ConfirmDelete = "ConfirmDelete",
    DeleteUnavailable = "DeleteUnavailable",
}

interface IMeetingServiceProps {
    orderId: number
    orderUid: string
    orderAction: IMeetingServiceDetailOrderAction
    handleEditOrder: () => void
}

type ConfirmCancelProps = Readonly<{
    loading: boolean
    open: boolean
    onClose: () => void
    onConfirm: () => void
}>

function ConfirmCancelModal({ loading, open, onClose, onConfirm }: ConfirmCancelProps) {
    return (
        <LocalizedStrict id="receipt-actions-confirm-cancel-dialog" attrs={{ description: true, confirmLabel: true, cancelLabel: true, loadingDescription: true }}>
            <ConfirmationDialog
                description="Er du sikker på, at du vil annullere din bestilling?"
                confirmLabel="Ja"
                cancelLabel="Nej"
                loading={loading}
                loadingDescription="Annullerer bestilling..."
                open={open}
                onClose={onClose}
                onConfirm={onConfirm}
            />
        </LocalizedStrict>
    )
}

type CancelUnavailableProps = Readonly<{
    cancelDetails: IMeetingServiceDetailAction
    open: boolean
    onClose: () => void
}>

function UpdateUnavailableModal({ cancelDetails, open, onClose }: CancelUnavailableProps) {
    return (
        <LocalizedStrict id="receipt-actions-cancel-unavailable-dialog" attrs={{ buttonLabel: true }}>
            <MessageDialog
                name="cancel-unavailable-modal"
                title={cancelDetails.message?.title || ''}
                message={cancelDetails.message?.body || ''}
                buttonLabel="Forstået"
                lottieAnim={OrderLockedAnim}
                open={open}
                onClose={onClose}
            />
        </LocalizedStrict>
    )
}

const MeetingServiceActions = ({ orderId, orderUid, orderAction, handleEditOrder }: IMeetingServiceProps) => {
    const [openDialog, setOpenDialog] = useState(ActionDialog.None)
    const [isOpen, setIsOpen] = useState(false)

    const token = useToken()
    const { currentEnv } = useEnvironment()
    const { handleTransactionResponse } = useTransactionHandling()

    const { loading: deleting, callForResult: callDeleteOrder, handleCallError } = useApiCall(deleteMeetingServiceOrder, {})

    function handleCloseModal() {
        setIsOpen(false)
    }

    function handleUpdateOrder() {
        handleCloseModal()
        if (orderAction?.updateMeetingService?.allow ?? false) {
            handleEditOrder()
        } else {
            setOpenDialog(ActionDialog.UpdateUnavailable)
        }
    }
    function handleConfirmDeleteOrder() {
        handleCloseModal()
        if (orderAction?.deleteOrder?.allow ?? false) {
            setOpenDialog(ActionDialog.ConfirmDelete)
        } else {
            setOpenDialog(ActionDialog.DeleteUnavailable)
        }
    }

    function handleDeleteOrder() {
        setOpenDialog(ActionDialog.None)
        callDeleteOrder(orderId, token, currentEnv)
            .then(response => handleTransactionResponse(response))
            .catch(e => handleCallError(e, "deleting order"))
    }

    return <>
        <HeadlessReceiptSection>
            {orderUid ? (
                <LocalizedStrict id="receipt-actions-forward-receipt-button">
                    <ReceiptAction onClick={() => setOpenDialog(ActionDialog.ForwardReceipt)}>
                        Send kvittering
                    </ReceiptAction>
                </LocalizedStrict>
            ) : (
                <ReceiptSkeleton />
            )}
            {orderAction ?
                orderAction?.updateMeetingService && (
                    <LocalizedStrict id="receipt-actions-edit-order-button">
                        <ReceiptAction onClick={() => setIsOpen(true)}>
                            Rediger
                        </ReceiptAction>
                    </LocalizedStrict>
                ) : (
                    <ReceiptSkeleton />
                )
            }
        </HeadlessReceiptSection>
        <OptionsDialog
            open={isOpen}
            onClose={handleCloseModal}
        >
            <LocalizedStrict id="receipt-actions-update-order-button">
                <ActionSheetButtonItem onClick={handleUpdateOrder}>
                    Rediger ordre
                </ActionSheetButtonItem>
            </LocalizedStrict>
            <LocalizedStrict id="receipt-actions-delete-order-button">
                <ActionSheetButtonItem onClick={handleConfirmDeleteOrder}>
                    Slet ordre
                </ActionSheetButtonItem>
            </LocalizedStrict>
        </OptionsDialog>
        {orderUid && (
            <ForwardReceiptDrawer
                name="detailed-receipt-forward"
                orderUids={[orderUid]}
                open={openDialog === ActionDialog.ForwardReceipt}
                onClose={() => setOpenDialog(ActionDialog.None)}
            />
        )}
        {orderAction && (
            <ConfirmCancelModal
                loading={deleting}
                open={openDialog === ActionDialog.ConfirmDelete}
                onClose={() => setOpenDialog(ActionDialog.None)}
                onConfirm={handleDeleteOrder}
            />
        )}
        {openDialog === ActionDialog.UpdateUnavailable && (<UpdateUnavailableModal
            cancelDetails={orderAction.updateMeetingService}
            open={openDialog === ActionDialog.UpdateUnavailable}
            onClose={() => setOpenDialog(ActionDialog.None)}
        />)}
        {openDialog === ActionDialog.DeleteUnavailable && (<UpdateUnavailableModal
            cancelDetails={orderAction.deleteOrder}
            open={openDialog === ActionDialog.DeleteUnavailable}
            onClose={() => setOpenDialog(ActionDialog.None)}
        />)}
    </>
}

export default memo(MeetingServiceActions)
